import * as React from 'react';
import { compose, setDisplayName, withState, withHandlers, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { HeatMapZone } from 'logic/reports/HeatMapZone';
import { drawZonesOnCanvas } from './drawZonesOnCanvas';
import { hitTestZones, HitTestResult } from './hitTestZones';
import { calculateDrawData, DrawData } from 'ui/shared/calculateDrawData';
import { imageElementFromBase64Jpeg } from 'ui/shared/imageElementFromBase64Jpeg';

import './ZoneSelection.css';

export type OwnProps = {
    zones: ReadonlyArray<HeatMapZone>;
    selectedZoneName: string | null;
    image: string | null;
    onChange: (name: string, z: HeatMapZone) => void;
    onSelect: (name: string | null) => void;
    onClickZoneName: () => void;
};

const RawZoneSelection = ({
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleClick,
    styles,
}: RecomposeProps & OemProps) =>
    <canvas
        id="zone-selection"
        style={ styles.Bordered }
        onMouseDown={ handleMouseDown }
        onMouseMove={ handleMouseMove }
        onMouseUp={ handleMouseUp }
        onClick={ handleClick }
    />
;

const getCanvas = () => document.getElementById('zone-selection') as HTMLCanvasElement | null;

type MouseDownData = {
    hitTest: HitTestResult;
    x: number;
    y: number;
};

type RecomposeProps = {
    backgroundImage: HTMLImageElement | null;
    handleMouseDown: React.MouseEventHandler<HTMLCanvasElement>;
    handleMouseMove: React.MouseEventHandler<HTMLCanvasElement>;
    handleMouseUp: React.MouseEventHandler<HTMLCanvasElement>;
    handleClick: React.MouseEventHandler<HTMLCanvasElement>;
    handleLoadImage: () => void;
};
type _RecomposeProps = RecomposeProps & {
    _editZone: HeatMapZone | null;
    _redraw: boolean;
    _drawData: DrawData;
    _mouseDown: MouseDownData | null;
    _setEditZone: (z: HeatMapZone | null) => void;
    _setRedraw: (b: boolean) => void;
    _setDrawData: (d: DrawData) => void;
    _setMouseDown: (d: MouseDownData | null) => void;
    _setBackgroundImage: (i: HTMLImageElement | null) => void;
};

const cursorByDragHandle = [ 'nw-resize', 'ne-resize', 'sw-resize', 'se-resize' ];

const setCursor = (canvas: HTMLCanvasElement, result: HitTestResult) => {
    if (result) {
        switch (result.hit) {
            case 'name':
                canvas.style.cursor = 'pointer';
                break;
            case 'handle':
                canvas.style.cursor = cursorByDragHandle[result.dragHandle!];
                break;
            case 'zone':
                canvas.style.cursor = 'move';
                break;
        }
    }
    else {
        canvas.style.cursor = 'default';
    }
};

// Must treat the zones array and zone elements as immutable... so we need to
// create a new array with a new element when a zone is being edited.
const replaceZone = (zones: ReadonlyArray<HeatMapZone>, editZone: HeatMapZone | null) =>
    editZone
        ? zones.map(z => z.name === editZone.name ? editZone : z)
        : zones
;

export const ZoneSelection = compose<OwnProps & RecomposeProps & OemProps & TranslateProps, OwnProps>(
    setDisplayName('ZoneSelection'),
    skin,
    translate(),
    withState('backgroundImage', '_setBackgroundImage', null),
    withState('_redraw', '_setRedraw', true),
    withState('_drawData', '_setDrawData', calculateDrawData(null, null)),
    withState('_mouseDown', '_setMouseDown', null),
    withState('_editZone', '_setEditZone', null),
    withHandlers<OwnProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleMouseDown: ({ zones, _drawData, _setMouseDown, onSelect, _setRedraw }) => (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = getCanvas();
            if (!canvas) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            const x = Math.round((e.pageX - rect.left - _drawData.xOffset) / _drawData.scale);
            const y = Math.round((e.pageY - rect.top - _drawData.yOffset) / _drawData.scale);

            const hitTest = hitTestZones(x, y, canvas, _drawData, zones);
            setCursor(canvas, hitTest);
            _setMouseDown(hitTest && { hitTest, x, y });

            onSelect(hitTest && hitTest.zone && hitTest.zone.name);
        },
        handleMouseMove: ({ zones, backgroundImage, _setEditZone, _drawData, _mouseDown, _setMouseDown, _setRedraw }) => (e: React.MouseEvent<HTMLCanvasElement>) => { // tslint:disable-line
            const canvas = getCanvas();
            if (!canvas) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            const x = Math.round((e.pageX - rect.left - _drawData.xOffset) / _drawData.scale);
            const y = Math.round((e.pageY - rect.top - _drawData.yOffset) / _drawData.scale);

            if (_mouseDown) { // User is dragging something
                const { hit, zone, dragHandle } = _mouseDown.hitTest!;
                const xOffset = x - _mouseDown.x;
                const yOffset = y - _mouseDown.y;
                const xMax = backgroundImage ? backgroundImage.width : Math.round(canvas.width / _drawData.scale);
                const yMax = backgroundImage ? backgroundImage.height : Math.round(canvas.height / _drawData.scale);
                if (hit === 'zone') {
                    _setEditZone(zone.offset(xOffset, yOffset, xMax, yMax));
                }
                else if (hit === 'handle') {
                    _setEditZone(zone.movePoint(dragHandle!, x, y, xMax, yMax));
                }
                _setRedraw(true);
            }
            else { // User is just moving the mouse
                const hitTest = hitTestZones(x, y, canvas, _drawData, zones);
                setCursor(canvas, hitTest);
            }
        },
        handleMouseUp: ({ onChange, _editZone, _mouseDown, _setMouseDown, _setEditZone, _setRedraw }) => (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (_mouseDown && _mouseDown.hitTest && _editZone) {
                // Reorder the points in case the user crossed the lines
                if (typeof _mouseDown.hitTest.dragHandle !== 'undefined') {
                    _editZone = _editZone.reorder();
                }
                onChange(_editZone.name, _editZone);
            }
            _setMouseDown(null);
            _setEditZone(null);
            _setRedraw(true);
        },
        handleClick: ({ zones, _drawData, onClickZoneName, _setRedraw, onSelect }) => (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = getCanvas();
            if (!canvas) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            const x = Math.round((e.pageX - rect.left - _drawData.xOffset) / _drawData.scale);
            const y = Math.round((e.pageY - rect.top - _drawData.yOffset) / _drawData.scale);

            const hitTest = hitTestZones(x, y, canvas, _drawData, zones);

            onSelect(hitTest && hitTest.zone && hitTest.zone.name);

            if (hitTest && hitTest.hit === 'name') {
                onClickZoneName();
            }

            _setRedraw(true);
        },
        handleLoadImage: ({ image, _setBackgroundImage, _setRedraw, _setDrawData }) => async () => {
            const img = await imageElementFromBase64Jpeg(image);
            _setBackgroundImage(img);

            const canvas = getCanvas();
            const drawData = calculateDrawData(canvas, img);
            _setDrawData(drawData);
            _setRedraw(true);
        },
    }),
    lifecycle<OwnProps & _RecomposeProps, {}>({
        componentDidMount() {
            const canvas = getCanvas();
            if (canvas) {
                // Set the dimensions (or else the drawings will be at the wrong scale)
                canvas.width = canvas.clientWidth;
                canvas.height = canvas.clientHeight;
                
                const { _setDrawData } = this.props;
                const drawData = calculateDrawData(canvas, null);
                _setDrawData(drawData);
            }

            // Start loading the image if we were given a base64 image string
            const { image, handleLoadImage } = this.props;
            if (image) {
                handleLoadImage();
            }
        },
        componentDidUpdate(oldProps) {
            const { zones, selectedZoneName, _redraw, _setRedraw, image, handleLoadImage } = this.props;

            // Redraw if the incoming zones object is different than the previous one
            // or if the selected zone changed
            if (zones !== oldProps.zones || selectedZoneName !== oldProps.selectedZoneName) {
                _setRedraw(true);
            }

            // image is the base64 image string but we need the actual image element
            // to paint onto the canvas. So if the image string changed, start the
            // process of reading the string into an <img/>
            if (image !== oldProps.image) {
                handleLoadImage();
            }

            // Only redraw when explicitly told to do so
            if (!_redraw) {
                return;
            }
            _setRedraw(false);
                
            const { backgroundImage, _editZone, _drawData } = this.props;

            const zonesToDraw = replaceZone(zones, _editZone);
            const canvas = getCanvas();
            drawZonesOnCanvas(canvas, _drawData, backgroundImage, zonesToDraw, selectedZoneName);
        },
    }),
)(RawZoneSelection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/zoneSelection/ZoneSelection.tsx